import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {IMeshCellValueFormat} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/ValueFormat';
import {CellNotesSortOrder, IOrderedMeshCellNote, MeshCellNote} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellNote';
import {
    CellSettingsSortOrder,
    IOrderedMeshCellSettings,
    MeshCellSettings
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellSettings';
import {MeshLocation, MeshLocationKey} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {MeshRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshRange';
import {MeshSheetSize} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshSheetSize';
import {AnySheetSettings, SheetSettingsId} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetSettingsTypes';
import {SheetId, SlotId} from '@mintdata/mint-kit/api/models/Identifiers';
import {FrameCellCoordinate, IFrameCoordinate} from '@mintdata/mint-kit/api/values/frames/Frame';
import {action} from 'mobx';

export abstract class AbstractSheetSettingsController
{
    protected abstract _getSheetSettings(sheetSettingsId:SheetSettingsId):AnySheetSettings | null;

    protected abstract _removeSheetSettings(sheetSettingsId:SheetSettingsId):void

    public abstract createSheetSettings(sheetId:SheetId):AnySheetSettings;

    public abstract getCellSettings(sheetId:SheetId, cellKey:MeshLocationKey):MeshCellSettings | null;

    public abstract getCellSettingsBySheetSettingsId(sheetSettingsId:SheetSettingsId, cellKey:MeshLocationKey):MeshCellSettings | null;

    public abstract setCellSettings(sheetId:SheetId, cellKey:MeshLocationKey, cellSettings:MeshCellSettings):void;

    public abstract getCellNote(sheetId:SheetId, cellKey:MeshLocationKey):MeshCellNote | null;

    public abstract getCellNoteBySheetSettingsId(sheetSettingsId:SheetSettingsId, cellKey:MeshLocationKey):MeshCellNote | null;

    public abstract setCellNote(sheetId:SheetId, cellKey:MeshLocationKey, cellNote:MeshCellNote):void;

    public abstract getCellFormat(sheetId:SheetId, cellKey:MeshLocationKey):IMeshCellValueFormat | null;

    public abstract getCellFormatBySheetSettingsId(sheetSettingsId:SheetSettingsId, cellKey:MeshLocationKey):IMeshCellValueFormat | null;

    public abstract setCellFormat(sheetId:SheetId, cellRange:MeshRange, sheetSize:MeshSheetSize, formatId:string):void;

    public getSheetSettings(sheetId:SheetId):AnySheetSettings | null
    {
        const sheetSettingsId = this.buildSheetSettingsIdBySheetId(sheetId);

        return this._getSheetSettings(sheetSettingsId);
    }

    public removeSheetSettings(sheetId:SheetId):void
    {
        const sheetSettingsId = this.buildSheetSettingsIdBySheetId(sheetId);

        return this._removeSheetSettings(sheetSettingsId);
    }

    @action
    public getOrCreateSheetSettings(sheetId:SheetId):AnySheetSettings
    {
        const sheetSettings = this.getSheetSettings(sheetId);

        return sheetSettings || this.createSheetSettings(sheetId);
    }

    public getOrCreateCellSettings(sheetId:SheetId, cellKey:MeshLocationKey):MeshCellSettings | null
    {
        const cellSettings = this.getCellSettings(sheetId, cellKey);

        if( !cellSettings )
        {
            this.setCellSettings(sheetId, cellKey, MeshCellSettings.create());
            return this.getCellSettings(sheetId, cellKey);
        }

        return cellSettings;
    }

    // public getOrCreateCellNote(sheetId:SheetId, cellKey:MeshLocationKey):MeshCellNote | null
    // {
    //     const cellNote = this.getCellNote(sheetId, cellKey);
    //
    //     if( !cellNote )
    //     {
    //         this.setCellNote(sheetId, cellKey, {} as MeshCellNote);
    //         return this.getCellNote(sheetId, cellKey);
    //     }
    //
    //     return cellNote;
    // }

    public getOrCreateCellFormat(sheetId:SheetId, cellKey:MeshLocationKey):IMeshCellValueFormat | null
    {
        return this.getCellFormat(sheetId, cellKey);
    }

    public getOrderedCellSettings(sheetId:SheetId,
                                  sortOrder:CellSettingsSortOrder = CellSettingsSortOrder.ASC):IOrderedMeshCellSettings[]
    {
        const sheetSettings = this.getSheetSettings(sheetId);

        if( !sheetSettings )
            return [];

        return Array.from(sheetSettings.cellSettings.keys())
            .map((key:MeshLocationKey) =>
            {
                return {
                    location: MeshLocation.fromKey(key),
                    cellSettings: this.getCellSettings(sheetId, key)
                };
            })
            .sort((aCellSettings:IOrderedMeshCellSettings, bCellSettings:IOrderedMeshCellSettings):number =>
            {
                const {colIndex: aColIndex, rowIndex: aRowIndex} = aCellSettings.location;
                const {colIndex: bColIndex, rowIndex: bRowIndex} = bCellSettings.location;

                if( aColIndex < bColIndex )
                    return sortOrder * -1;

                if( aColIndex > bColIndex )
                    return sortOrder;

                if( aRowIndex < bRowIndex )
                    return sortOrder * -1;

                if( aRowIndex > bRowIndex )
                    return sortOrder;

                return 0;
            });
    }

    public getOrderedCellNotes(sheetId:SheetId,
                               sortOrder:CellNotesSortOrder = CellNotesSortOrder.ASC):IOrderedMeshCellNote[]
    {
        const sheetSettings = this.getSheetSettings(sheetId);

        if( !sheetSettings )
            return [];

        return Array.from(sheetSettings.cellNotes.keys())
            .map((key:MeshLocationKey) =>
            {
                return {
                    location: MeshLocation.fromKey(key),
                    cellNote: this.getCellNote(sheetId, key)
                };
            })
            .sort((aCellNote:IOrderedMeshCellNote, bCellNote:IOrderedMeshCellNote):number =>
            {
                const {colIndex: aColIndex, rowIndex: aRowIndex} = aCellNote.location;
                const {colIndex: bColIndex, rowIndex: bRowIndex} = bCellNote.location;

                if( aColIndex < bColIndex )
                    return sortOrder * -1;

                if( aColIndex > bColIndex )
                    return sortOrder;

                if( aRowIndex < bRowIndex )
                    return sortOrder * -1;

                if( aRowIndex > bRowIndex )
                    return sortOrder;

                return 0;
            });
    }

    @action
    public copyCellSettings(sheetId:SheetId, fromCellKey:MeshLocationKey, toCellKey:MeshLocationKey):MeshCellSettings
    {
        const cellSettings = this.getCellSettings(sheetId, fromCellKey);
        const copyCellSettings = cellSettings ? cellSettings.clone() : MeshCellSettings.create();

        this.setCellSettings(sheetId, toCellKey, copyCellSettings);

        return copyCellSettings;
    }

    @action
    public copyCellNote(sheetId:SheetId, fromCellKey:MeshLocationKey, toCellKey:MeshLocationKey):MeshCellNote
    {
        const cellNote = this.getCellNote(sheetId, fromCellKey);
        const copyCellNote = cellNote ? cellNote.clone() : MeshCellNote.create();

        this.setCellNote(sheetId, toCellKey, copyCellNote);

        return copyCellNote;
    }

    public buildSheetSettingsIdBySheetId(sheetId:SheetId):SheetSettingsId
    {
        return sheetId;
    }

    public buildSheetSettingsIdBySlotId(slotId:SlotId, framePath:Array<IFrameCoordinate>):SheetSettingsId
    {
        const pathString = this._framePathToString(framePath);
        return pathString.length > 0 ? `${slotId}_${pathString}` : slotId;
    }

    protected _getCellSettings(sheetSettings:AnySheetSettings, cellKey:MeshLocationKey):MeshCellSettings | null
    {
        const cellSettingsId = sheetSettings.cellSettings.get(cellKey);

        if( !cellSettingsId )
            return null;

        const {main} = commonControllers.spreadsheets;

        const sheet = main.findAnySheetById(sheetSettings.sheetId);
        const workbook = main.findWorkbookById(sheet.workbook.id);

        return workbook.view.getCellSettings(cellSettingsId);
    }

    protected _getCellNote(sheetSettings:AnySheetSettings, cellKey:MeshLocationKey):MeshCellNote | null
    {
        const cellNoteId = sheetSettings.cellNotes.get(cellKey);

        if( !cellNoteId )
            return null;

        const {main} = commonControllers.spreadsheets;

        const sheet = main.findAnySheetById(sheetSettings.sheetId);
        const workbook = main.findWorkbookById(sheet.workbook.id);

        return workbook.view.getCellNote(cellNoteId);
    }

    private _framePathToString(framePath:Array<IFrameCoordinate>):string
    {
        return framePath
            ? framePath.map((coordinate:FrameCellCoordinate) => `'${coordinate.fieldName}'[${coordinate.rowIndex}]`)
                .join('.')
            : '';
    }
}
